import type {
  AuthResponse,
  RefreshApiResponse,
  SetCredentialsRequest,
  StatusApiResponse,
  User,
  UserRole,
} from '@/types';
import axiosClient from './axiosClient';

import { API_ENDPOINTS, MESSAGES } from '@/constants';

export function mapBackendUserToUser(data): User {
  const idValue = data.appUserId || data.id || data.userId || '';
  const email = data.email || undefined;
  const phone = data.phone || data.phoneNumber || undefined;

  const walletAddr = data.wallet?.eoaAddress || data.walletAddress || data.eoaAddress;
  const addressFragment = walletAddr
    ? walletAddr.replace('0x', '').substring(0, 4)
    : String(idValue);
  const defaultName = phone ? `User-${addressFragment}` : 'User';

  const user: User = {
    id: idValue ? String(idValue) : '',
    email,
    phone,
    name: data.displayName || data.name || email?.split('@')[0] || defaultName,
    role: (data.role as UserRole) ?? 'user',
    avatar: data.photoUrl || data.avatar,
    emailVerified: !!data.emailVerified,
    createdAt: data.createdAt ?? new Date().toISOString(),
    updatedAt: data.updatedAt ?? new Date().toISOString(),
    clobCredentials: data.clobCredentials,
    isAmbassador: !!data.isAmbassador,
    isReferredUser: !!data.isReferredUser,
    referralCode: data.referralCode,
  };

  if (walletAddr) user.walletAddress = walletAddr;

  const depositAddr = data.wallet?.depositAddress;
  if (depositAddr) user.depositAddress = depositAddr;

  const proxyAddr = data.proxyWalletAddress || data.proxyAddress;
  if (proxyAddr) user.proxyWalletAddress = proxyAddr;

  const kind = data.tradingWalletKind || data.proxyWallet?.tradingWalletKind;
  if (kind === 'gnosis_safe' || kind === 'deposit_wallet') {
    user.tradingWalletKind = kind;
  }

  return user;
}

export const authApi = {
  login: async (
    idToken: string,
    userName?: string,
    referralCode?: string,
  ): Promise<AuthResponse> => {
    const { data } = await axiosClient.post<any>(API_ENDPOINTS.AUTH.LOGIN, {
      privyToken: idToken,
      userName: userName,
      referralCode: referralCode,
    });
    if (!data || data.success === false) {
      throw new Error(MESSAGES.AUTH.LOGIN_FAILED);
    }
    const payload = data.data ?? data;
    const user = mapBackendUserToUser(payload);

    return {
      user,
      accessToken: payload.accessToken ?? payload.access_token,
      refreshToken:
        payload.refreshToken ??
        payload.refresh_token ??
        payload.accessToken ??
        payload.access_token,
    };
  },

  /**
   * Logout (backend API)
   */
  logout: async (): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.AUTH.LOGOUT);
  },

  /**
   * Get auth status and user (backend API)
   * Maps backend user format (appUserId) to app User format (id).
   * When backend returns authenticated: true but empty user {}, builds minimal User from JWT.
   */
  getStatus: async (): Promise<{ authenticated: boolean; user: User | null }> => {
    const { data: responseData } = await axiosClient.get<StatusApiResponse>(
      API_ENDPOINTS.AUTH.STATUS,
    );
    if (!responseData.authenticated) {
      return { authenticated: false, user: null };
    }

    const rawUser = responseData.data;

    const user = mapBackendUserToUser(rawUser);
    if (!user.id) {
      return { authenticated: true, user: null };
    }

    return { authenticated: true, user };
  },

  /**
   * Refresh access token (backend API - takes current refreshToken)
   */
  refreshToken: async (
    refreshToken: string,
  ): Promise<{ accessToken: string; refreshToken?: string }> => {
    const { data } = await axiosClient.post<RefreshApiResponse>(API_ENDPOINTS.AUTH.REFRESH, {
      refreshToken,
    });
    if (!data.success || !data.data) {
      throw new Error(MESSAGES.API.TOKEN_REFRESH_FAILED);
    }
    // Return the rotated refresh token too so callers can persist it (sliding
    // session). Fall back to the current one if the backend omitted it.
    return {
      accessToken: data.data.accessToken,
      refreshToken: data.data.refreshToken ?? refreshToken,
    };
  },

  /**
   * Set Polymarket CLOB credentials (secure storage)
   */
  setCredentials: async (payload: SetCredentialsRequest): Promise<void> => {
    await axiosClient.post(API_ENDPOINTS.AUTH.SET_CREDENTIALS, payload);
  },

  /**
   * Update User Display Name
   */
  updateDisplayName: async (displayName: string): Promise<User> => {
    const { data } = await axiosClient.patch(API_ENDPOINTS.AUTH.UPDATE_DISPLAY_NAME, {
      displayName,
    });
    const payload = data.data ?? data;
    return mapBackendUserToUser(payload);
  },
};
